import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import { useAuth } from 'firebaseHooks/AuthContext';
import { useFirestore } from 'firebaseHooks/FirestoreContext';
import {
  doc, getDoc,
  addDoc,
  collection,
} from 'firebase/firestore';
import useGlobal from 'global-state/store';
import {
  BrowserRouter, Routes, Route, useNavigate, useLocation,
} from 'react-router-dom';
import cgu from 'staticFiles/CGU.pdf';
import cgv from 'staticFiles/CGV.pdf';
import Organizations from 'components/organizations/Organizations';
import { useTranslation } from 'react-i18next';
import GlobalSnackbar from 'components/GlobalSnackbar';
import RemoteConfigStatus from 'components/RemoteConfigStatus';
import Cookies from 'components/cookies/Cookies';
import Contact from 'components/contact/Contact';
import { logEvent, getAnalytics } from 'firebase/analytics';
import PDFView from 'components/PDFView';
import MyAccount from 'components/account/MyAccount';
import Organization from 'components/organizations/Organization';
import SignInPage from './SignInPage';
import TopButtons from './TopButtons';
import Footer from './Footer';
import LoadingPage from './LoadingPage';

export default function Home() {
  return <RemoteConfigStatus defaultComponent={<UserSetupMenuAndMainPage />} />;
}

function UserSetupMenuAndMainPage() {
  const { currentUser, initializing } = useAuth();
  const db = useFirestore();
  const [globalState, globalActions] = useGlobal();
  const analytics = getAnalytics();

  useEffect(() => {
    // eslint-disable-next-line no-promise-executor-return
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    async function getUser(uid) {
      const userRef = doc(db, 'users', uid);
      const firestoreUser = await getDoc(userRef);
      return firestoreUser;
    }
    async function getUserPrivateData(uid) {
      const userRef = doc(db, 'users', uid, 'private', 'data');
      const firestoreUser = await getDoc(userRef);
      return firestoreUser;
    }
    async function fetchMultipleTimesUser(uid) {
      let firestoreUser = await getUser(uid);
      if (!firestoreUser.exists()) {
        await sleep(1000);
        firestoreUser = await getUser(uid);
        if (!firestoreUser.exists()) {
          await await sleep(3000);
          firestoreUser = await getUser(uid);
        }
      }
      return firestoreUser;
    }

    async function setupGlobalStateFromUser() {
      globalActions.setUserStatus('loading');
      const firestoreUser = await fetchMultipleTimesUser(currentUser.uid);
      const userData = firestoreUser.data();
      if (userData) {
        if (userData.laboxWSRoles) {
          globalActions.setUserWSRoles(userData.laboxWSRoles);
        }
      }
      const firestoreUserPrivateData = await getUserPrivateData(currentUser.uid);
      const userPrivateData = firestoreUserPrivateData.data();
      globalActions.setOrganizations(userPrivateData.organizations);
      globalActions.setActiveOrganization(userPrivateData.activeOrganization);
      logEvent(analytics, 'initial_user_setup', {
        appName: 'Organizations-WS',
        organization: userPrivateData.activeOrganization,
        userUid: currentUser.uid,
      });
      return globalActions.setUserStatus('setupDone');
    }

    if (currentUser?.uid !== undefined && globalState.userStatus === 'initial') {
      setupGlobalStateFromUser();
    }
  }, [analytics, db, globalActions, globalState.networkState, globalState.userStatus, currentUser?.uid]);

  if (initializing) {
    return (
      <LoadingPage />
    );
  }

  return <MainPage />;
}

function MainPage() {
  const { t } = useTranslation();
  const [, globalActions] = useGlobal();

  return (
    <BrowserRouter>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          minHeight: '80vh', display: 'flex', flexDirection: 'column',
        }}
        >
          <GlobalSnackbar />
          <TopButtons />
          <MainPageContent />
        </Box>
        <Footer />
        <Cookies
          cookieName="agreed-to-cookies-on-organizations-ws"
        />
      </Box>
    </BrowserRouter>
  );
}

function MainPageContent(props) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [globalState, globalActions] = useGlobal();
  const db = useFirestore();
  const analytics = getAnalytics();

  useEffect(() => {
    if (!currentUser
      && location.pathname !== '/contact'
      && location.pathname !== '/cgu') {
      navigate('/signin');
    }
  }, [location.pathname, navigate, currentUser]);

  const sendEmail = async (values) => {
    const emailContent = `Email: 
      ${values.email}

      Nom et entreprise: 
      ${values.nameAndCompany}

      Message: 
      ${values.message}`;

    await addDoc(
      collection(db, 'mail'),
      {
        to: 'contact@labox-apps.com',
        message: {
          subject: `[WS Organizations, Contact] ${values.nameAndCompany}`,
          text: emailContent,
        },
      },
    );
  };
  if (globalState.userStatus !== 'setupDone') {
    return (
      <Routes>
        <Route path="/" element={<Organizations />} />
        <Route exact path="signin" element={<SignInPage />} />
        <Route exact path="account" element={<MyAccount />} />
        <Route
          path="contact"
          element={(
            <Contact
              sendEmail={sendEmail}
            />
  )}
        />
        <Route path="cgu" element={<PDFView path={cgu} />} />
        <Route path="cgv" element={<PDFView path={cgv} />} />
        <Route
          path="*"
          element={(
            <main style={{ padding: '1rem' }}>
              {/* eslint-disable-next-line react/no-unescaped-entities */}
              <p>There's nothing here!</p>
            </main>
              )}
        />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Organizations />} />
      <Route exact path="organization" element={<Organization />} />
      <Route exact path="signin" element={<SignInPage />} />
      <Route exact path="account" element={<MyAccount />} />
      <Route
        path="contact"
        element={(
          <Contact
            t={t}
            navigate={navigate}
            globalActions={globalActions}
            analytics={analytics}
            sendEmail={sendEmail}
            signInCheckResult={{ signedIn: !!currentUser, user: currentUser }}
          />
)}
      />
      <Route path="cgu" element={<PDFView path={cgu} />} />
      <Route path="cgv" element={<PDFView path={cgv} />} />
      <Route
        path="*"
        element={(
          <main style={{ padding: '1rem' }}>
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            <p>There's nothing here!</p>
          </main>
            )}
      />
    </Routes>
  );
}
